import { useNavigate, useParams, useLocation } from "react-router-dom";
import { logger } from "@/infrastructure/lib/logger";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/components/ui/accordion";
import { toast } from "sonner";
import { cn } from "@/infrastructure/lib/utils";
import {
  AlertTriangle,
  ArrowLeft,
  Plus,
  Copy,
  Pencil,
  X,
  Trash2,
  Loader2,
  Eye,
  Upload,
  Lock,
  AlertCircle,
  Info,
} from "lucide-react";
import { Alert, AlertDescription } from "@/shared/components/ui/alert";
import { JobsService } from "@/features/Jobs/services/jobsService";
import { TooltipProvider } from "@/shared/components/ui/tooltip";
import MDEditor from "@uiw/react-md-editor";
import { AgentIntegrations } from "./AgentIntegrations";
import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { useWorkspace } from "@/shared/hooks/api/useWorkspace";
import { useIsSuperAdminRole } from "@/shared/hooks/use-system-role";
import { useIsAdmin } from "@/shared/hooks/use-admin-validation";
import { useTheme } from "@/shared/hooks/use-theme";
import { AgentBase } from "../types/AgentBaseInterface";
import { Prompt } from "../types/AgentPrompts";
import { AgentWorkflow } from "./AgentWorkflow";
import { ProdgyWorkflowEditor } from "./ProdgyWorkflowEditor";
import type { WorkflowEditorRef } from "@/features/Workflows/components/WorkflowEditor";
import { AgentParameters } from "./AgentParameters";
import { AgentVersions } from "./AgentVersions";
import { AgentExecutions } from "./AgentExecutions";
import { PublishVersionModal } from "./PublishVersionModal";
import { AgentBaseService } from "../services/agentBaseService";
import { AgentIntegrationsService } from "../services/agentIntegrationsService";
import { WorkspaceService } from "@/features/Workspaces/services/workspaceService";
import { Progress } from "@/shared/components/ui/progress";
import {
  translateDeep,
  getIntegrationDescription,
} from "@/shared/utils/i18nHelper";
import { analyticsService } from "@/shared/services/analytics";

interface AgentWithWorkflow extends AgentBase {
  workflow_schema?: Record<string, unknown>;
  workflow_settings?: Record<string, unknown>;
}

interface AgentIntegrationDisplay {
  id: string;
  name: string;
  label: string;
  description: string;
  logo_url: string | null;
  classification: string;
  status: string;
  agent_integration_id: string;
  updated_at?: string;
  url?: string;
}

export interface VersionRestoredHandlerParams {
  setWorkflowRefreshKey: React.Dispatch<React.SetStateAction<number>>;
  queryClient: QueryClient;
  agentId?: string;
}

// Local interface matching AgentParameters internal ParameterData/ParameterField shape
type AgentParameterValue =
  | string
  | number
  | boolean
  | Record<string, unknown>
  | null
  | undefined;
interface AgentParameterField {
  name: string;
  type: string;
  label: string;
  required: boolean;
  default?: AgentParameterValue;
  options?: Array<{ label: string; value: string }> | string[];
  value?: AgentParameterValue;
}
interface AgentParametersData {
  fields?: AgentParameterField[];
  values?: Record<string, AgentParameterValue>;
  saveParameters?:
    | (() =>
        | { success: boolean; skipped?: boolean; missingFields?: string[] }
        | false)
    | null;
}

// eslint-disable-next-line react-refresh/only-export-components
export const buildVersionRestoredHandler = ({
  setWorkflowRefreshKey,
  queryClient,
  agentId,
}: VersionRestoredHandlerParams) => {
  return () => {
    setWorkflowRefreshKey((prev) => prev + 1);

    if (agentId) {
      queryClient.invalidateQueries({ queryKey: ["agent", agentId] });
    }
  };
};

const AgentSettings = () => {
  const params = useParams();

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const currentLang = i18n.language.split("-")[0]; // 'pt' ou 'en'

  // Mapear idioma i18n para formato de locale
  const getLocale = () => {
    const lang = i18n.language;
    if (lang.startsWith("pt")) return "pt-BR";
    if (lang.startsWith("en")) return "en-US";
    return "en-US"; // Fallback padrão
  };

  // Alterando para usar o parâmetro "id" que está definido na rota
  const agentId = params.id;
  const queryClient = useQueryClient();
  const [isIntegrationsDialogOpen, setIsIntegrationsDialogOpen] =
    useState(false);
  const [selectedPromptIndex, setSelectedPromptIndex] = useState<number>(0);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState(false);
  const [editingTitleIndex, setEditingTitleIndex] = useState<number | null>(
    null,
  );
  const [editingTitleValue, setEditingTitleValue] = useState("");
  const isInitialLoad = useRef(true); // Flag para detectar carregamento inicial
  const isMounted = useRef(false); // Flag para detectar primeira montagem vs recarregamentos
  const productLastIdRef = useRef<string | null>(null);
  const isFirstProductCheckRef = useRef(true);
  const originalPromptsRef = useRef<Prompt[]>([]); // Armazena prompts originais do banco
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const [agent, setAgent] = useState<AgentBase | null>(null);
  // Um job (gatilho + uma Skill, AB#86605) também abre aqui; a aba Workflow avisa
  // que acrescentar passos o transforma em flow. Um flow responde 409 e fica sem aviso.
  const { data: jobInfo } = useQuery({
    queryKey: ["job-kind", currentWorkspace?.id, agentId],
    queryFn: () => JobsService.get(currentWorkspace!.id, agentId!),
    enabled: Boolean(currentWorkspace?.id && agentId && agent?.executor === "prodgy"),
    retry: false,
    staleTime: 60_000,
  });
  const isJob = jobInfo?.kind === "job";
  const [currentTab, setCurrentTab] = useState("integrations");
  const previousTab = useRef<string>("integrations"); // Rastrear aba anterior
  const { isSuperAdmin } = useIsSuperAdminRole();
  const { isAdmin } = useIsAdmin();
  const { theme } = useTheme();
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [workflowRefreshKey, setWorkflowRefreshKey] = useState(0);
  const ignoreWorkflowCallback = useRef(false); // Flag para ignorar callback após publicação
  const allowNavigation = useRef(false); // Flag para permitir navegação após descartar alterações
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<
    string | number | null
  >(null);

  // Rastrear alterações por aba para tooltip do badge
  const [tabChanges, setTabChanges] = useState<{
    integrations: boolean;
    parameters: boolean;
    prompts: boolean;
    workflow: boolean;
  }>({
    integrations: false,
    parameters: false,
    prompts: false,
    workflow: false,
  });

  // Traduzir settings do agent
  const translatedAgent = useMemo(() => {
    if (!agent) return null;
    return {
      ...agent,
      settings: agent.settings
        ? translateDeep(agent.settings, currentLang)
        : agent.settings,
    };
  }, [agent, currentLang]);
  // Estados para o processo de desinstalação
  const [confirmAgentName, setConfirmAgentName] = useState("");
  const [isUninstallDialogOpen, setIsUninstallDialogOpen] = useState(false);
  const [isUninstalling, setIsUninstalling] = useState(false);
  const [uninstallProgress, setUninstallProgress] = useState(0);
  // Ref para acessar o método saveParameters do componente
  const parametersRef = useRef<{
    saveParameters?: () =>
      | { success: boolean; skipped?: boolean; missingFields?: string[] }
      | false;
  }>({});

  // Ref para acessar o método saveWorkflow do WorkflowEditor
  const workflowRef = useRef<WorkflowEditorRef | null>(null);

  // Estado local para manter alterações de parâmetros entre trocas de aba
  const [localParameters, setLocalParameters] =
    useState<AgentParametersData | null>(null);

  const handleCopyPrompt = useCallback(() => {
    navigator.clipboard.writeText(systemPrompt);
    toast.success(t("common.copied"));
  }, [systemPrompt, t]);

  const handleVersionRestored = useMemo(() => {
    const baseHandler = buildVersionRestoredHandler({
      setWorkflowRefreshKey,
      queryClient,
      agentId,
    });
    return () => {
      baseHandler();
      // Limpar a flag de alterações não publicadas após restaurar versão
      setHasUnpublishedChanges(false);
      // Limpar estados locais de alterações
      setLocalParameters(null);
      // Limpar indicadores de alterações nas abas
      setTabChanges({
        integrations: false,
        parameters: false,
        prompts: false,
        workflow: false,
      });
      // Resetar flags de carregamento inicial
      isInitialLoad.current = true;
      isMounted.current = false;
    };
  }, [setWorkflowRefreshKey, queryClient, agentId]);

  // Logo que falha ao carregar cai para as iniciais — por estado, e resetado
  // quando a url muda, senão um agente novo herdaria a falha do anterior.
  const [logoFailed, setLogoFailed] = useState(false);
  useEffect(() => {
    setLogoFailed(false);
  }, [agent?.logo_url]);

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Query para buscar o agente
  const { data: fetchedAgent, isLoading: isAgentLoading } = useQuery({
    queryKey: ["agent", agentId],
    queryFn: async () => {
      if (!agentId) {
        throw new Error("Agent ID não encontrado na URL");
      }

      const { data, error } = await AgentBaseService.getAgentBase(agentId);

      if (error) throw error;
      if (!data) throw new Error("Agente não encontrado");

      // Inicializa o estado do prompt com o valor do banco APENAS na primeira carga
      // Não sobrescrever se já há alterações não publicadas (protege estado local)
      if (!isMounted.current) {
        if (data.settings?.prompts) {
          const prompts = data.settings.prompts as Prompt[];
          setPrompts(prompts);
          originalPromptsRef.current = JSON.parse(JSON.stringify(prompts)); // Deep copy
          if (prompts.length > 0) {
            setSelectedPromptIndex(0);
            setSystemPrompt(prompts[0].content);
          }
        } else {
          setPrompts([]);
          originalPromptsRef.current = [];
        }
      }

      // Só limpar badge na primeira montagem, não em recarregamentos
      if (!isMounted.current) {
        setHasUnpublishedChanges(false);
        isMounted.current = true;
      }

      // Resetar flag para evitar detecção de mudanças no carregamento
      isInitialLoad.current = true;

      // Marcar que não é mais carregamento inicial após 500ms
      setTimeout(() => {
        isInitialLoad.current = false;
      }, 500);

      return data;
    },
  });

  useEffect(() => {
    if (fetchedAgent) {
      setAgent(fetchedAgent);
    }
  }, [fetchedAgent]);

  useEffect(() => {
    const currentWorkspaceId = currentWorkspace?.id || null;

    if (isFirstProductCheckRef.current) {
      isFirstProductCheckRef.current = false;
      productLastIdRef.current = currentWorkspaceId;
      return;
    }

    if (productLastIdRef.current && !currentWorkspaceId) {
      navigate("/workflows", { replace: true });
      productLastIdRef.current = null;
      return;
    }

    if (
      productLastIdRef.current &&
      currentWorkspaceId &&
      currentWorkspaceId !== productLastIdRef.current
    ) {
      navigate("/workflows", { replace: true });
    }

    productLastIdRef.current = currentWorkspaceId;
  }, [currentWorkspace?.id, navigate]);

  // Query para buscar as integrações do agente via agent_integrations
  const { data: integrations, isError: integrationsFailed } = useQuery({
    queryKey: ["agent-integrations", agent?.id],
    enabled: !!agent?.id,
    queryFn: async () => {
      if (!agent?.id) return [];

      // Busca as integrações vinculadas ao agente (include_details → integration_base embutido).
      const response = await AgentIntegrationsService.getByAgentId(
        agent.id,
        true,
      );

      if (!response.data || response.data.length === 0) {
        return [];
      }
      const integrations = response.data.map((agentIntegration) => {
        // O integration_base vem embutido na resposta.
        const base = agentIntegration.integration_base;

        return {
          id: agentIntegration.integration_base_id,
          name: base?.name || agentIntegration.integration_name,
          label: base?.label || agentIntegration.integration_name,
          description: base
            ? getIntegrationDescription(base, currentLang) || ""
            : "",
          logo_url: base?.logo_url || null,
          classification: base?.classification || "",
          status: base?.status || "active",
          // Mantém compatibilidade com a estrutura esperada
          agent_integration_id: agentIntegration.id, // ID da relação para remoção
        };
      });

      return integrations;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (
      updatedAgent: Partial<AgentBase> & {
        _trackingTab?: string;
        _silent?: boolean;
      },
    ) => {
      const { _trackingTab, _silent, ...agentData } = updatedAgent;

      const { error } = await AgentBaseService.updateAgentBase(
        agentId as string,
        agentData,
      );
      if (error) {
        throw error;
      }
      return { ...agentData, _trackingTab, _silent };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["agent", agentId] });

      // Só mostrar toast se não for salvamento silencioso
      if (!data?._silent) {
        toast.success(i18n.t("agents.settings.updateSuccess"), {
          description: i18n.t("agents.settings.updateSuccessDescription"),
        });
      }

      // Track settings updated with specific tab
      if (agent) {
        const trackingTab = data?._trackingTab || currentTab || "general";
        analyticsService.trackEvent("agent_settings_updated", {
          agent_name: agent.name,
          agent_id: agent.id,
          settings_changed: [trackingTab],
        });
      }
    },
    onError: (_error) => {
      toast.error(i18n.t("common.error"), {
        description: i18n.t("agents.settings.updateErrorDescription"),
      });
    },
  });

  const handleUpdate = (field: string, value: unknown) => {
    // Ao invés de salvar direto no banco, apenas atualiza o estado local
    // As alterações serão persistidas apenas ao publicar
    const newSettings: Record<string, unknown> = { ...agent?.settings };

    // Checar se é uma atualização de subcampo (ex: basic.type)
    if (field.includes(".")) {
      const [category, key] = field.split(".");
      const existingCategory =
        newSettings[category] && typeof newSettings[category] === "object"
          ? (newSettings[category] as Record<string, unknown>)
          : {};
      newSettings[category] = { ...existingCategory, [key]: value };
    } else {
      // Se não tem ponto, é uma atualização direta em settings
      newSettings[field] = value;
    }

    // Atualiza apenas o estado local do agent
    if (agent) {
      setAgent({ ...agent, settings: newSettings });
    }

    // Marca que há alterações não publicadas
    setHasUnpublishedChanges(true);

    // Atualizar alterações específicas da aba
    setTabChanges((prev) => ({
      ...prev,
      [currentTab]: true,
    }));
  };

  const handleAddIntegration = async (integrationId: string) => {
    if (!agent?.id) return;

    try {
      // Criar relacionamento via AgentIntegrationsService
      await AgentIntegrationsService.create({
        agent_base_id: agent.id,
        integration_base_id: integrationId,
        created_by: user?.id,
      });

      // Invalidar queries para atualizar a lista
      queryClient.invalidateQueries({
        queryKey: ["agent-integrations", agent.id],
      });
      queryClient.invalidateQueries({ queryKey: ["agent", agent.id] });
      // Invalidar queries do IntegrationSelector para atualizar dropdowns nos workflows
      queryClient.invalidateQueries({
        queryKey: ["integration-selector", agent.id],
      });

      // Marcar que há alterações na aba de integrações
      setHasUnpublishedChanges(true);
      setTabChanges((prev) => ({ ...prev, integrations: true }));

      toast.success(t("agents.settings.integrations.addedSuccessfully"));
    } catch (error) {
      toast.error(t("agents.settings.integrations.addError"));
    }
  };

  useEffect(() => {
    // Só inicializar prompts na primeira carga (quando ainda não foi montado)
    // Não sobrescrever se já há alterações locais não publicadas
    if (!isMounted.current && agent?.settings?.prompts) {
      const agentPrompts = agent.settings.prompts as Prompt[];
      setPrompts(agentPrompts);
      if (agentPrompts.length > 0) {
        setSelectedPromptIndex(0);
        setSystemPrompt(agentPrompts[0].content);
      }
    }
  }, [agent]);

  useEffect(() => {
    if (prompts[selectedPromptIndex]) {
      setSystemPrompt(prompts[selectedPromptIndex].content);
      // Não limpar badge aqui - trocar de seção não significa que não há alterações
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPromptIndex]);

  // Rastrear mudança de abas
  useEffect(() => {
    if (previousTab.current !== currentTab) {
      previousTab.current = currentTab;
    }
  }, [currentTab]);

  const handleAddPromptSection = () => {
    const newPrompt: Prompt = {
      title: i18n.t("agents.settings.prompts.untitled"),
      content: "",
      updatedAt: new Date().toISOString(),
    };
    setPrompts([...prompts, newPrompt]);
    setSelectedPromptIndex(prompts.length);
    setSystemPrompt("");
    setHasUnpublishedChanges(true);
    setTabChanges((prev) => ({ ...prev, prompts: true }));
  };

  const handleUpdatePromptTitle = (index: number, title: string) => {
    const updatedPrompts = prompts.map((p, i) =>
      i === index ? { ...p, title, updatedAt: new Date().toISOString() } : p,
    );
    setPrompts(updatedPrompts);

    // Verificar se houve alteração real comparando com original (ignorando updatedAt)
    const comparePrompts = (p1: Prompt[], p2: Prompt[]) => {
      if (p1.length !== p2.length) return false;
      return p1.every(
        (prompt, idx) =>
          prompt.title === p2[idx]?.title &&
          prompt.content === p2[idx]?.content &&
          prompt.block === p2[idx]?.block,
      );
    };

    const hasChanges = !comparePrompts(
      updatedPrompts,
      originalPromptsRef.current,
    );
    if (hasChanges) {
      setHasUnpublishedChanges(true);
      setTabChanges((prev) => ({ ...prev, prompts: true }));
    }
  };

  const handleUpdatePromptContent = (content: string) => {
    setSystemPrompt(content);
    const updatedPrompts = prompts.map((p, i) =>
      i === selectedPromptIndex
        ? { ...p, content, updatedAt: new Date().toISOString() }
        : p,
    );
    setPrompts(updatedPrompts);

    // Verificar se houve alteração real comparando com original (ignorando updatedAt)
    const comparePrompts = (p1: Prompt[], p2: Prompt[]) => {
      if (p1.length !== p2.length) return false;
      return p1.every(
        (prompt, idx) =>
          prompt.title === p2[idx]?.title &&
          prompt.content === p2[idx]?.content &&
          prompt.block === p2[idx]?.block,
      );
    };

    const hasChanges = !comparePrompts(
      updatedPrompts,
      originalPromptsRef.current,
    );
    if (hasChanges) {
      setHasUnpublishedChanges(true);
      setTabChanges((prev) => ({ ...prev, prompts: true }));
    }
  };

  const handleDeletePromptSection = (index: number) => {
    if (prompts.length === 1) {
      toast.error(i18n.t("common.error"), {
        description: i18n.t("agents.settings.prompts.cannotDeleteLast"),
      });
      return;
    }

    const newPrompts = prompts.filter((_, i) => i !== index);
    setPrompts(newPrompts);

    if (selectedPromptIndex === index) {
      const newIndex = index > 0 ? index - 1 : 0;
      setSelectedPromptIndex(newIndex);
      setSystemPrompt(newPrompts[newIndex].content);
    } else if (selectedPromptIndex > index) {
      setSelectedPromptIndex(selectedPromptIndex - 1);
    }

    setHasUnpublishedChanges(true);
    setTabChanges((prev) => ({ ...prev, prompts: true }));
  };

  const showSaveButton = [
    "integrations",
    "prompts",
    "parameters",
    "workflow",
  ].includes(currentTab);

  // Lidar com navegação de volta com verificação de alterações não salvas
  const handleBackNavigation = () => {
    if (hasUnpublishedChanges) {
      setShowUnsavedChangesDialog(true);
    } else {
      navigate(-1);
    }
  };

  // Descartar alterações e navegar
  const handleDiscardChanges = () => {
    // Salvar navegação pendente antes de limpar estados
    const targetPath = pendingNavigation;

    // IMPORTANTE: Permitir navegação antes de limpar estados
    allowNavigation.current = true;

    // Limpar TODOS os estados primeiro
    setShowUnsavedChangesDialog(false);
    setPendingNavigation(null);
    setHasUnpublishedChanges(false);
    setTabChanges({
      integrations: false,
      parameters: false,
      prompts: false,
      workflow: false,
    });
    setLocalParameters(null);

    // Resetar flags
    isInitialLoad.current = true;
    isMounted.current = false;
    originalPromptsRef.current = [];

    // Recarregar dados do agente
    queryClient.invalidateQueries({ queryKey: ["agent", agentId] });

    // Navegar IMEDIATAMENTE usando setTimeout para garantir que os estados foram limpos
    setTimeout(() => {
      if (
        targetPath === -1 ||
        targetPath === "-1" ||
        (typeof targetPath === "number" && targetPath < 0)
      ) {
        window.history.back();
      } else if (targetPath && typeof targetPath === "string") {
        navigate(targetPath);
      } else {
        navigate(-1);
      }
      // Resetar flag após navegação
      setTimeout(() => {
        allowNavigation.current = false;
      }, 100);
    }, 0);
  };

  // Continuar editando
  const handleContinueEditing = () => {
    setShowUnsavedChangesDialog(false);
    setPendingNavigation(null);
  };

  // Bloquear navegação do navegador (refresh, fechar aba, etc)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnpublishedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnpublishedChanges]);

  // Bloquear botão voltar/avançar do navegador
  useEffect(() => {
    // Adicionar entrada no histórico quando há alterações não salvas
    if (hasUnpublishedChanges && !allowNavigation.current) {
      window.history.pushState(null, "", window.location.href);
    }

    const handlePopState = (_e: PopStateEvent) => {
      // Se navegação foi autorizada, permitir
      if (allowNavigation.current) return;

      if (hasUnpublishedChanges) {
        // Prevenir navegação adicionando entrada no histórico novamente
        window.history.pushState(null, "", window.location.href);

        // Mostrar modal de confirmação
        setShowUnsavedChangesDialog(true);
        setPendingNavigation(-1); // -1 indica navegação para trás
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [hasUnpublishedChanges]);

  // Bloquear navegação interna (React Router e links)
  useEffect(() => {
    if (!hasUnpublishedChanges) return;

    // Interceptar TODOS os cliques no documento
    const handleClick = (e: MouseEvent) => {
      // Se navegação foi autorizada, permitir
      if (allowNavigation.current) return;

      const target = e.target as HTMLElement;

      // Procurar por qualquer elemento clicável que possa causar navegação
      const clickable = target.closest(
        'a, button, [role="button"], [role="link"], [data-nav]',
      );

      if (clickable) {
        const href =
          clickable.getAttribute("href") ||
          clickable.getAttribute("data-href") ||
          clickable.getAttribute("to");

        // Se tem href e não é externo/âncora
        if (
          href &&
          !href.startsWith("http") &&
          !href.startsWith("#") &&
          href !== location.pathname
        ) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          setPendingNavigation(href);
          setShowUnsavedChangesDialog(true);
          return false;
        }

        // Se é um botão/link sem href mas que pode navegar (ex: onClick com navigate)
        const onClick = (clickable as HTMLElement).onclick;
        if (
          onClick &&
          clickable.textContent?.toLowerCase().includes("conhecimento")
        ) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          setShowUnsavedChangesDialog(true);
          return false;
        }
      }
    };

    // Capturar na fase de captura (antes de qualquer handler)
    document.addEventListener("click", handleClick, true);

    // Também interceptar o history.pushState do React Router
    const originalPushState = window.history.pushState;
    window.history.pushState = function (...args) {
      // Se navegação foi autorizada, permitir
      if (allowNavigation.current) {
        return originalPushState.apply(this, args);
      }

      const url = args[2];
      if (url && typeof url === "string" && url !== location.pathname) {
        setPendingNavigation(url);
        setShowUnsavedChangesDialog(true);
        return;
      }
      return originalPushState.apply(this, args);
    };

    return () => {
      document.removeEventListener("click", handleClick, true);
      window.history.pushState = originalPushState;
    };
  }, [hasUnpublishedChanges, location.pathname]);

  // validateVersionName removida - não é mais necessária com PublishVersionModal

  // Função handlePublish removida - agora usa PublishVersionModal

  // Função para desinstalar um agente
  const handleUninstallAgent = async () => {
    if (!agent) {
      toast.error(t("agents.errors.invalidAgent"));
      return;
    }

    // Obter o nome do produto do agente
    try {
      const workspaceData = await WorkspaceService.getById(
        agent.product_id || "",
      );

      // Verificamos se o nome do produto foi digitado corretamente
      if (confirmAgentName !== workspaceData.name) {
        toast.error(t("agents.errors.workspaceNameNotMatch"));
        return;
      }

      // Inicia processo de desinstalação
      setIsUninstallDialogOpen(false);
      setIsUninstalling(true);
      setUninstallProgress(0);

      // Simula progresso da desinstalação
      const progressInterval = setInterval(() => {
        setUninstallProgress((prev) => {
          const newProgress = prev + Math.random() * 15;
          // Limita o progresso a 90% durante a simulação
          return Math.min(newProgress, 90);
        });
      }, 200);

      try {
        if (!agent.id) {
          throw new Error("ID do agente não disponível para desinstalação");
        }

        // Desinstalando agente

        const { error } = await AgentBaseService.uninstallAgent(agent.id);

        // Limpa o intervalo de progresso
        clearInterval(progressInterval);

        if (error) {
          setIsUninstalling(false);
          setUninstallProgress(0);

          toast.error(error.message || t("agents.errors.uninstallFailed"));
          return;
        }

        // Track successful uninstall
        analyticsService.trackEvent("agent_uninstalled", {
          agent_name: agent.name,
        });

        // Fase final: 95% - Atualizando lista para UX otimizada
        setUninstallProgress(95);

        try {
          // Aguardar processamento no backend antes de atualizar cache
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // Invalidar cache específico com workspace ID
          await queryClient.invalidateQueries({
            queryKey: ["agent_base", agent.product_id],
            exact: true,
          });

          // Implementar retry para aguardar o status ser atualizado no backend
          let retryCount = 0;
          const maxRetries = 4;
          let agentUninstalled = false;

          while (retryCount < maxRetries && !agentUninstalled) {
            // Forçar refetch
            const queries = queryClient.getQueriesData({
              queryKey: ["agent_base", agent.product_id],
            });

            if (queries.length > 0) {
              await Promise.all(
                queries.map(([queryKey]) =>
                  queryClient.refetchQueries({ queryKey, exact: true }),
                ),
              );

              // Verificar se o agente não aparece mais como instalado
              const freshData = queryClient.getQueryData([
                "agent_base",
                agent.product_id,
              ]);
              if (freshData && Array.isArray(freshData)) {
                const currentAgent = (freshData as AgentBase[]).find(
                  (a) => a.id === agent.id,
                );

                if (!currentAgent || currentAgent.status !== "installed") {
                  agentUninstalled = true;
                  break;
                }
              }
            }

            retryCount++;
            if (retryCount < maxRetries) {
              await new Promise((resolve) => setTimeout(resolve, 800));
            }
          }

          if (!agentUninstalled) {
            // ignored
          }

          // Completa o progresso para 100%
          setUninstallProgress(100);

          // Aguarda a animação completar antes de mostrar o toast e redirecionar
          setTimeout(() => {
            setIsUninstalling(false);
            setUninstallProgress(0);

            toast.success(t("agents.uninstallSuccess", { name: agent.name }));

            // Redireciona para a página de agentes (lista já estará atualizada)
            navigate("/workflows");
          }, 400);
        } catch (cacheError) {
          // Fallback: mesmo com erro no cache, completar o processo
          setUninstallProgress(100);

          setTimeout(async () => {
            setIsUninstalling(false);
            setUninstallProgress(0);

            toast.success(t("agents.uninstallSuccess", { name: agent.name }));

            // Fallback: invalida cache específico no redirecionamento e aguarda
            try {
              await queryClient.invalidateQueries({
                queryKey: ["agent_base", agent.product_id],
                exact: true,
              });
              await new Promise((resolve) => setTimeout(resolve, 200));
            } catch (e) {
              // ignored
            }

            navigate("/workflows");
          }, 400);
        }
      } catch (err) {
        // Limpa o intervalo de progresso
        clearInterval(progressInterval);

        setIsUninstalling(false);
        setUninstallProgress(0);

        toast.error(t("agents.errors.uninstallFailed"));
      }
    } catch (err) {
      toast.error(t("agents.errors.workspaceDataError"));
    }
  };

  if (isAgentLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Componente interno para exibir o nome do produto
  const WorkspaceNameDisplay = ({ workspaceId }: { workspaceId: string }) => {
    const { data: workspace, isLoading } = useQuery({
      queryKey: ["workspace", workspaceId],
      queryFn: async () => {
        const workspace = await WorkspaceService.getById(workspaceId);
        return workspace;
      },
      enabled: !!workspaceId,
    });

    if (isLoading) {
      return null;
    }

    if (!workspace) {
      return null;
    }

    return (
      <span>
        {t("agents.settings.uninstallButton.typeName")}:{" "}
        <strong>{workspace.name}</strong>
      </span>
    );
  };

  if (!agent) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          {t("agents.settings.loading")}
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="container mx-auto py-6">
        <div className="flex flex-col gap-8">
          {/* Header */}
          <div className="flex items-end justify-between gap-6">
            <div className="flex items-center gap-4 border-b">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBackNavigation}
                className="h-12 w-12 border border-input"
                title={t("common.actionsList.back")}
              >
                <ArrowLeft className="h-6 w-6" />
              </Button>

              {/* Avatar and Info Section */}
              <div className="flex items-center gap-6 py-4">
                <div className="relative group">
                  <div className="h-24 w-24 rounded-full bg-primary/5 ring-4 ring-background shadow-xl overflow-hidden">
                    {/* Fallback por estado, não por `innerHTML` no elemento pai:
                        aquele nó é do React, e reescrevê-lo por fora faz o
                        próximo render duplicar ou apagar a inicial. */}
                    {agent?.logo_url && !logoFailed ? (
                      <img
                        src={agent.logo_url}
                        alt={agent.name}
                        className="w-full h-full object-cover"
                        onError={() => setLogoFailed(true)}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-primary/10 text-primary font-medium text-2xl">
                        {getInitials(agent.name)}
                      </div>
                    )}
                  </div>
                  {/* Status Indicator */}
                  {agent?.status === "active" && (
                    <div className="absolute bottom-1 right-1 h-4 w-4 rounded-full bg-emerald-500 ring-2 ring-background" />
                  )}
                </div>

                {/* Agent Info */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-semibold">{agent?.name}</h2>
                    {hasUnpublishedChanges && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200">
                        <AlertCircle className="h-3.5 w-3.5" />
                        {t("agents.settings.unsavedChanges")}
                      </span>
                    )}
                  </div>
                  <p className="text-muted-foreground text-base max-w-md">
                    {
                      (
                        translatedAgent?.settings?.basic as
                          | Record<string, unknown>
                          | undefined
                      )?.description as string | undefined
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Publish Button */}
            {showSaveButton && (
              <Button
                type="button"
                disabled={isAgentLoading}
                onClick={async (e) => {
                  e.preventDefault();

                  let saveSuccess = true;

                  if (currentTab === "parameters") {
                    const saveParametersFn =
                      parametersRef.current.saveParameters;

                    if (typeof saveParametersFn === "function") {
                      const result = saveParametersFn();
                      const isSuccess =
                        result && typeof result === "object"
                          ? result.success
                          : result;

                      if (!isSuccess) {
                        saveSuccess = false;
                        if (
                          result &&
                          typeof result === "object" &&
                          result.missingFields
                        ) {
                          const missingFieldsList =
                            result.missingFields.join(", ");
                          toast.error(
                            t("agents.settings.parameters.missingRequired"),
                            {
                              description: `${t("agents.settings.parameters.missingFields")}: ${missingFieldsList}`,
                            },
                          );
                        } else {
                          toast.error(
                            t("agents.settings.parameters.saveError"),
                          );
                        }
                      } else if (agent?.settings) {
                        // Persiste os parâmetros editados no agent_base (paridade com a aba prompts).
                        // Sem isto, o publish só cria a versão; o agent_base mantém os params antigos
                        // e o refetch pós-publish reverte a UI (a tela lê da linha, não da versão).
                        try {
                          const updatedSettings = {
                            ...agent.settings,
                            parameters:
                              localParameters ?? agent.settings.parameters,
                          };
                          await updateMutation.mutateAsync({
                            settings: updatedSettings,
                            _trackingTab: "parameters",
                            _silent: true,
                          });
                        } catch (error) {
                          saveSuccess = false;
                        }
                      }
                    } else {
                      saveSuccess = false;
                      toast.error(t("agents.settings.parameters.saveError"));
                    }
                  } else if (agent?.settings) {
                    try {
                      if (currentTab === "prompts") {
                        const updatedSettings = {
                          ...agent.settings,
                          prompts: prompts,
                        };
                        await updateMutation.mutateAsync({
                          settings: updatedSettings,
                          _trackingTab: currentTab,
                          _silent: true,
                        });
                      } else {
                        await updateMutation.mutateAsync({
                          settings: agent.settings,
                          _trackingTab: currentTab,
                          _silent: true,
                        });
                      }
                    } catch (error) {
                      // O toast para o usuário vem do `onError` da mutation; aqui
                      // fica o motivo técnico, que antes se perdia, e o publish
                      // para — o modal não abre com o agente meio salvo.
                      logger.error(
                        "[AgentSettings] Falha ao salvar settings antes de publicar",
                        error,
                      );
                      saveSuccess = false;
                    }
                  }

                  if (saveSuccess && workflowRef.current?.saveWorkflow) {
                    try {
                      await workflowRef.current.saveWorkflow();
                    } catch (error) {
                      // Não bloquear publicação se workflow falhar - modal buscará do banco
                    }
                  }

                  if (saveSuccess) {
                    setShowPublishModal(true);
                  }
                }}
                className="shrink-0"
              >
                {isAgentLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("agents.settings.publishButton.publishing")}
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    {t("agents.settings.publishButton.label")}
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Tabs Section */}
          <Tabs
            defaultValue="integrations"
            value={currentTab}
            onValueChange={setCurrentTab}
            className="space-y-4"
          >
            <TabsList>
              <TabsTrigger value="integrations" className="relative">
                {t("agents.settings.tabs.integrations")}
                {tabChanges.integrations && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 flex h-5 w-5 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900 border-2 border-background z-10">
                    <AlertCircle className="h-3.5 w-3.5 text-yellow-700 dark:text-yellow-400" />
                  </span>
                )}
              </TabsTrigger>
              {!!agent?.settings?.parameters && (
                <TabsTrigger value="parameters" className="relative">
                  {t("agents.settings.tabs.parameters")}
                  {tabChanges.parameters && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 flex h-5 w-5 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900 border-2 border-background z-10">
                      <AlertCircle className="h-3.5 w-3.5 text-yellow-700 dark:text-yellow-400" />
                    </span>
                  )}
                </TabsTrigger>
              )}
              <TabsTrigger value="prompts" className="relative">
                {t("agents.settings.tabs.prompts")}
                {tabChanges.prompts && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 flex h-5 w-5 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900 border-2 border-background z-10">
                    <AlertCircle className="h-3.5 w-3.5 text-yellow-700 dark:text-yellow-400" />
                  </span>
                )}
              </TabsTrigger>
              {isSuperAdmin && (
                <TabsTrigger value="workflow" className="relative">
                  {t("agents.settings.tabs.workflow")}
                  {tabChanges.workflow && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 flex h-5 w-5 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900 border-2 border-background z-10">
                      <AlertCircle className="h-3.5 w-3.5 text-yellow-700 dark:text-yellow-400" />
                    </span>
                  )}
                </TabsTrigger>
              )}
              <TabsTrigger value="versions">
                {t("agents.settings.tabs.versions")}
              </TabsTrigger>
              <TabsTrigger value="executions">
                {t("agents.settings.tabs.executions")}
              </TabsTrigger>
              {/* Desinstalar o agente é irreversível e afeta a organização inteira. */}
              {isAdmin && (
                <TabsTrigger value="dangerZone">
                  {t("agents.settings.tabs.dangerZone")}
                </TabsTrigger>
              )}
            </TabsList>

            {/* Integrations Tab */}
            <TabsContent value="integrations" className="space-y-4">
              <div className="grid gap-4 p-4 border rounded-lg">
                {integrationsFailed && (
                  <p className="text-sm text-destructive">
                    {t("common.messages.errorDescription")}
                  </p>
                )}
                <Accordion type="single" collapsible className="space-y-4">
                  {integrations?.map(
                    (integration: AgentIntegrationDisplay, index: number) => (
                      <AccordionItem
                        key={integration.agent_integration_id}
                        value={`item-${index}`}
                        className="border rounded-lg overflow-hidden"
                      >
                        <AccordionTrigger className="hover:no-underline px-4 py-3 flex flex-1 items-center justify-between [&[data-state=open]>svg]:rotate-180">
                          <div className="flex-1 flex items-center gap-4">
                            {integration?.logo_url ? (
                              <img
                                src={integration.logo_url}
                                alt={integration.name}
                                className="w-10 h-10 rounded-lg object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                                {integration.name[0]?.toUpperCase()}
                              </div>
                            )}
                            <div className="flex flex-col items-start">
                              <span className="font-medium text-base">
                                {integration.label || integration.name}
                              </span>
                              <span className="text-sm text-muted-foreground">
                                {getIntegrationDescription(
                                  integration,
                                  currentLang,
                                ) || i18n.t("agents.settings.noDescription")}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={
                                integration.status === "active"
                                  ? "success"
                                  : integration.status === "inactive"
                                    ? "secondary"
                                    : integration.status === "pending"
                                      ? "warning"
                                      : "destructive"
                              }
                              className="ml-2"
                            >
                              {t(
                                `agents.settings.integrations.status.${integration.status}`,
                              )}
                            </Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="border-t">
                          <div className="space-y-4 p-4">
                            <div className="grid grid-cols-2 gap-6">
                              <div>
                                <label className="text-sm font-medium">
                                  {i18n.t(
                                    "agents.settings.integrations.lastUpdated",
                                  )}
                                </label>
                                <div className="mt-1 text-sm text-muted-foreground">
                                  {integration.updated_at
                                    ? new Date(
                                        integration.updated_at,
                                      ).toLocaleString(getLocale())
                                    : i18n.t(
                                        "agents.settings.integrations.neverUpdated",
                                      )}
                                </div>
                              </div>
                              {integration.url && (
                                <div>
                                  <label className="text-sm font-medium">
                                    {i18n.t(
                                      "agents.settings.integrations.integrationUrl",
                                    )}
                                  </label>
                                  <div className="mt-1 text-sm text-muted-foreground">
                                    <a
                                      href={integration.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-primary hover:underline"
                                    >
                                      {integration.url}
                                    </a>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ),
                  )}
                </Accordion>
              </div>
            </TabsContent>

            {/* Prompts Tab */}
            <TabsContent value="prompts" className="space-y-4">
              <div className="border rounded-lg p-4">
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold">
                        {t("agents.settings.prompts.title")}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCopyPrompt}
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        {t("agents.settings.prompts.copy")}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsPreviewMode(!isPreviewMode)}
                      >
                        {isPreviewMode ? (
                          <>
                            <Pencil className="h-4 w-4 mr-2" />
                            {t("agents.settings.prompts.edit")}
                          </>
                        ) : (
                          <>
                            <Eye className="h-4 w-4 mr-2" />
                            {t("agents.settings.prompts.preview")}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-[250px_1fr] divide-x">
                  {/* Lista de Seções */}
                  <div className="p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium">
                        {t("agents.settings.prompts.title")}
                      </h4>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleAddPromptSection}
                        className="h-8 w-8"
                        title={t("agents.settings.prompts.add")}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>

                    {prompts.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        {t("agents.settings.prompts.noSections")}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {prompts.map((prompt, index) => (
                          <div
                            key={index}
                            className={cn(
                              "group flex items-center justify-between p-2 rounded-md hover:bg-muted/50 cursor-pointer",
                              selectedPromptIndex === index && "bg-muted",
                              prompt.block && "bg-muted/30",
                            )}
                            onClick={() => {
                              setSelectedPromptIndex(index);
                              setSystemPrompt(prompt.content || "");
                            }}
                          >
                            {prompt.block ? (
                              <span className="text-sm opacity-70 flex-1">
                                {prompt.title ||
                                  t("agents.settings.prompts.untitled")}
                              </span>
                            ) : editingTitleIndex === index ? (
                              <input
                                type="text"
                                value={editingTitleValue}
                                onChange={(e) =>
                                  setEditingTitleValue(e.target.value)
                                }
                                onBlur={() => {
                                  handleUpdatePromptTitle(
                                    index,
                                    editingTitleValue,
                                  );
                                  setEditingTitleIndex(null);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    handleUpdatePromptTitle(
                                      index,
                                      editingTitleValue,
                                    );
                                    setEditingTitleIndex(null);
                                  } else if (e.key === "Escape") {
                                    setEditingTitleIndex(null);
                                  }
                                }}
                                onFocus={(e) => e.target.select()}
                                autoFocus
                                className="text-sm bg-transparent border-none focus:outline-none focus:ring-0 w-full"
                                placeholder={t(
                                  "agents.settings.prompts.untitled",
                                )}
                              />
                            ) : (
                              <span
                                className="text-sm flex-1"
                                onDoubleClick={(e) => {
                                  e.stopPropagation();
                                  setEditingTitleValue(prompt.title);
                                  setEditingTitleIndex(index);
                                }}
                              >
                                {prompt.title ||
                                  t("agents.settings.prompts.untitled")}
                              </span>
                            )}
                            {!prompt.block && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeletePromptSection(index);
                                }}
                                className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                title={t("agents.settings.prompts.delete")}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                            {prompt.block && (
                              <Lock className="h-4 w-4 text-muted-foreground" />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Editor */}
                  <div data-color-mode={theme} className="p-4">
                    {prompts[selectedPromptIndex]?.block && (
                      <div className="mb-3 p-3 rounded-md bg-muted/50 border border-muted">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Lock className="h-4 w-4" />
                          <span>{t("agents.settings.prompts.locked")}</span>
                        </div>
                      </div>
                    )}
                    <div
                      className={cn(
                        "rounded-lg overflow-hidden border bg-background",
                        prompts[selectedPromptIndex]?.block &&
                          "pointer-events-none opacity-70",
                      )}
                    >
                      <MDEditor
                        value={systemPrompt}
                        onChange={(value) =>
                          !prompts[selectedPromptIndex]?.block &&
                          handleUpdatePromptContent(value || "")
                        }
                        preview={isPreviewMode ? "preview" : "edit"}
                        height={400}
                        className="w-full !bg-background"
                        previewOptions={{
                          className: "p-4",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Parameters Tab */}
            {!!agent?.settings?.parameters && (
              <TabsContent value="parameters" className="space-y-4">
                <AgentParameters
                  parameters={
                    (localParameters || agent?.settings?.parameters) as {
                      fields: AgentParameterField[];
                      values?: Record<string, AgentParameterValue>;
                    }
                  }
                  onChangeState={(hasChanges) => {
                    if (hasChanges) {
                      setHasUnpublishedChanges(true);
                      setTabChanges((prev) => ({ ...prev, parameters: true }));
                    }
                  }}
                  onLocalChange={(parametersData) => {
                    setLocalParameters(parametersData);
                  }}
                  onUpdate={(parametersData) => {
                    // Registra a função saveParameters através do ref
                    if (parametersData.saveParameters !== undefined) {
                      parametersRef.current.saveParameters =
                        parametersData.saveParameters ?? undefined;
                      return;
                    }

                    if (parametersData.fields || parametersData.values) {
                      setLocalParameters(parametersData);
                      handleUpdate("parameters", parametersData);
                    }
                  }}
                />
              </TabsContent>
            )}

            {/* Workflow Tab - Mantém sempre montado para preservar estado local */}
            {isSuperAdmin && agent && agent.executor === "prodgy" && (
              <div
                style={{
                  display: currentTab === "workflow" ? "block" : "none",
                }}
              >
                {isJob && (
                  <Alert className="mb-4">
                    <Info className="h-4 w-4" />
                    <AlertDescription>{t("jobs.settings.jobNotice")}</AlertDescription>
                  </Alert>
                )}
                <ProdgyWorkflowEditor
                  agent={agent}
                  onPublished={() => {
                    setHasUnpublishedChanges(false);
                    setTabChanges((prev) => ({ ...prev, workflow: false }));
                  }}
                />
              </div>
            )}
            {isSuperAdmin && agent && agent.executor !== "prodgy" && (
              <div
                style={{
                  display: currentTab === "workflow" ? "block" : "none",
                }}
              >
                <AgentWorkflow
                  agent={agent}
                  refreshKey={workflowRefreshKey}
                  onUpdate={(hasChanges) => {
                    // Ignorar callback logo após publicação
                    if (ignoreWorkflowCallback.current) {
                      return;
                    }
                    // Atualizar badge independente da aba atual
                    setHasUnpublishedChanges(hasChanges);
                    setTabChanges((prev) => ({
                      ...prev,
                      workflow: hasChanges,
                    }));
                  }}
                  organizationId={agent.organization_id}
                  workspaceId={agent.product_id}
                  saveRef={workflowRef}
                />
              </div>
            )}

            {/* Versions Tab */}
            <TabsContent value="versions">
              {agent && (
                <AgentVersions
                  agentId={agentId!}
                  onVersionRestored={handleVersionRestored}
                />
              )}
            </TabsContent>

            {/* Executions Tab */}
            <TabsContent value="executions">
              {agent && (
                <AgentExecutions
                  agentBaseId={agent.agent_base_id || agentId!}
                />
              )}
            </TabsContent>

            {/* Danger Zone Tab */}
            <TabsContent value="dangerZone" hidden={!isAdmin}>
              <div className="border border-destructive/20 rounded-lg p-6 bg-destructive/5">
                <div className="flex items-start gap-3 mb-6">
                  <div className="p-2 rounded-full bg-destructive/10">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-destructive">
                      {t("agents.settings.tabs.dangerZone")}
                    </h2>
                    <p className="text-muted-foreground mt-1">
                      {t("agents.settings.dangerZoneDescription")}
                    </p>
                  </div>
                </div>

                <div className="border-t border-destructive/20 pt-4 mt-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-medium">
                        {t("agents.settings.uninstallButton.label")}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {t("agents.confirmUninstallDescription")}
                      </p>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setIsUninstallDialogOpen(true)}
                      className="shrink-0"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {t("agents.uninstall")}
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Novo Modal de Publicação Simplificada */}
          {agent && (
            <PublishVersionModal
              isOpen={showPublishModal}
              onClose={() => setShowPublishModal(false)}
              onSuccess={() => {
                toast.success(t("agents.settings.publishButton.success"));
                setShowPublishModal(false);
                // Limpar badge de alterações não publicadas
                setHasUnpublishedChanges(false);
                // Limpar estados locais após publicação bem-sucedida
                setLocalParameters(null);
                // Reset tab changes
                setTabChanges({
                  integrations: false,
                  parameters: false,
                  prompts: false,
                  workflow: false,
                });
                // Ignorar callbacks do workflow pelos próximos 2 segundos
                ignoreWorkflowCallback.current = true;
                setTimeout(() => {
                  ignoreWorkflowCallback.current = false;
                }, 2000);
                // Resetar flags para que o próximo recarregamento não marque como alteração
                isMounted.current = false;
                isInitialLoad.current = true;
                originalPromptsRef.current = [];
                // Atualizar a lista de versões e recarregar agente
                queryClient.invalidateQueries({ queryKey: ["agent", agentId] });
                queryClient.invalidateQueries({
                  queryKey: ["agent-versions", agentId],
                });
              }}
              agentBaseId={agentId!}
              agentName={agent.name}
              workflowSchema={(agent as AgentWithWorkflow).workflow_schema}
              settings={{
                ...agent.settings,
                prompts,
                // Usar parâmetros locais se existirem, senão usar do agent
                parameters: localParameters || agent.settings?.parameters,
              }}
              workflowSettings={(agent as AgentWithWorkflow).workflow_settings}
              prompts={prompts}
              logoUrl={agent.logo_url}
              workspaceId={agent.product_id}
              createdBy={user?.id || ""}
            />
          )}

          {/* Unsaved Changes Dialog */}
          <Dialog
            open={showUnsavedChangesDialog}
            onOpenChange={(open) => {
              // Permitir fechar via X button (quando open=false)
              if (!open) {
                handleContinueEditing();
              }
            }}
          >
            <DialogContent
              onInteractOutside={(e) => {
                // Bloquear fechamento ao clicar fora
                e.preventDefault();
              }}
            >
              <DialogHeader>
                <DialogTitle>
                  {t("agents.settings.unsavedChangesDialog.title")}
                </DialogTitle>
                <DialogDescription>
                  {t("agents.settings.unsavedChangesDialog.description")}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={handleContinueEditing}>
                  {t("agents.settings.unsavedChangesDialog.continueEditing")}
                </Button>
                <Button variant="destructive" onClick={handleDiscardChanges}>
                  {t("agents.settings.unsavedChangesDialog.discardAndExit")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Uninstall Confirmation Dialog */}
          <Dialog
            open={isUninstallDialogOpen}
            onOpenChange={(open) => {
              setIsUninstallDialogOpen(open);
              if (!open) {
                setConfirmAgentName("");
              }
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {t("agents.settings.uninstallButton.confirmTitle")}
                </DialogTitle>
                <DialogDescription>
                  {t("agents.settings.uninstallButton.confirmDescription")}
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="confirmAgentName">
                    {t("agents.settings.uninstallButton.confirmAgentNameLabel")}
                  </Label>
                  <Input
                    id="confirmAgentName"
                    value={confirmAgentName}
                    onChange={(e) => setConfirmAgentName(e.target.value)}
                    placeholder={
                      agent?.product_id
                        ? ""
                        : t(
                            "agents.settings.uninstallButton.confirmAgentNamePlaceholder",
                          )
                    }
                  />
                  {agent?.product_id && (
                    <p className="text-xs text-muted-foreground mt-1">
                      <WorkspaceNameDisplay workspaceId={agent.product_id} />
                    </p>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsUninstallDialogOpen(false);
                    setConfirmAgentName("");
                  }}
                >
                  {t("agents.settings.uninstallButton.cancel")}
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleUninstallAgent}
                  disabled={isUninstalling}
                >
                  {isUninstalling ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("agents.settings.uninstallButton.uninstalling")}
                    </>
                  ) : (
                    <>
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t("agents.settings.uninstallButton.confirm")}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Uninstall Progress Dialog */}
          <Dialog
            open={isUninstalling}
            onOpenChange={(open) => {
              setIsUninstalling(open);
            }}
          >
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="text-center">
                  {t("agents.settings.uninstallButton.uninstallingTitle")}
                </DialogTitle>
                <DialogDescription className="text-center">
                  {t(
                    "agents.settings.uninstallButton.uninstallingDescription",
                    { name: agent?.name || "" },
                  )}
                </DialogDescription>
              </DialogHeader>
              <div className="py-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>
                        {t("agents.settings.uninstallButton.uninstallingStep")}
                      </span>
                      <span className="font-medium">
                        {Math.round(uninstallProgress)}%
                      </span>
                    </div>
                    <Progress value={uninstallProgress} className="h-2" />
                    <p className="text-xs text-center text-muted-foreground mt-2">
                      {uninstallProgress < 100
                        ? t(
                            "agents.settings.uninstallButton.uninstallingMessage",
                          )
                        : t(
                            "agents.settings.uninstallButton.uninstalledMessage",
                          )}
                    </p>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Dialog para selecionar integrações */}
          <AgentIntegrations
            open={isIntegrationsDialogOpen}
            onClose={() => setIsIntegrationsDialogOpen(false)}
            onSelect={(integration) => handleAddIntegration(integration.id)}
            selectedIntegrations={
              (agent?.settings?.integrations as string[] | undefined) || []
            }
          />
        </div>
      </div>
    </TooltipProvider>
  );
};

export default AgentSettings;
